import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { bubEvents } from 'javascripts/shared/events/bub_events';

import { ChirpButton } from 'javascripts/components/chirp_button';
import { AudiobookItemButton } from 'javascripts/components/audiobook_item_button';

import whitePlayIcon from 'images/play-icon-white';
import whitePauseIcon from 'images/pause-icon-white';
import playIcon from 'images/play-button';
import pauseIcon from 'images/pause';

export function PlaySampleButton(props) {
  const audioRef = useRef();
  const [samplePlayedAt, setSamplePlayedAt] = useState();
  const [sampleButtonText, setSampleButtonText] = useState(props.playText);

  const { audiobook, trackingLocation, shouldPause, backgroundColor, hoverColor, buttonStyle, origin } = props;
  const chirpPlayIcon = backgroundColor ? whitePlayIcon : playIcon;
  const chirpPauseIcon = backgroundColor ? whitePauseIcon : pauseIcon;

  const [icon, setIcon] = useState(chirpPlayIcon);

  useEffect(() => {
    if (shouldPause && !_.isNil(audiobook.sampleUrl)) {
      audioRef.current.pause();
      setSampleButtonText(props.playText);
      setIcon(chirpPlayIcon);
    }
  }, [shouldPause, audiobook]);

  function trackPlaySampleButtonClick() {
    setSamplePlayedAt(Date.now());

    bubEvents.track(trackingLocation, 'Clicked Play sample button', {
      label: 'Audiobook id',
      value: audiobook.id,
    });

    if (window.gtag !== undefined) {
      gtag('event', 'play_sample', {
        event_category: 'sample_listen',
        event_label: audiobook.displayTitle,
        non_interaction: true,
      });
    }
  }

  function trackPauseSampleButtonClick() {
    bubEvents.track(trackingLocation, 'Clicked Pause sample button', {
      label: 'Audiobook id',
      value: audiobook.id,
    });

    if (window.gtag !== undefined) {
      gtag('event', 'pause_sample', {
        event_category: 'sample_listen',
        event_label: audiobook.displayTitle,
        value: (Date.now() - samplePlayedAt) / 1000,
        non_interaction: true,
      });
    }
  }

  function onClick() {
    if (audioRef.current.paused) {
      trackPlaySampleButtonClick();
      audioRef.current.play();
      setSampleButtonText(props.pauseText);
      setIcon(chirpPauseIcon);
      props.onPlaySample();
    } else {
      trackPauseSampleButtonClick();
      audioRef.current.pause();
      setSampleButtonText(props.playText);
      setIcon(chirpPlayIcon);
      props.onPauseSample();
    }
  }

  function onEnded() {
    audioRef.current.currentTime = 0;
    setSampleButtonText(props.playText);
    setIcon(chirpPlayIcon);
    props.onPauseSample();
  }

  function renderButton() {
    if (buttonStyle === 'audiobookList') {
      return (
        <AudiobookItemButton
          className='play_sample_button'
          icon={icon}
          iconAltText={props.condensed ? sampleButtonText : ''}
          iconSize='small'
          onClick={props.libraryLink ? null : onClick}
          label={props.libraryLink ? 'Play Audiobook' : sampleButtonText}
          buttonColor={backgroundColor}
          hoverColor={hoverColor}
          href={props.libraryLink}
          buttonStyle={props.libraryLink ? 'default' : 'link'}
        />
      );
    }
    if (origin === 'audiobook_item') {
      return (
        <ChirpButton
          class='play_sample_button'
          icon={icon}
          iconAltText={props.condensed ? sampleButtonText : ''}
          iconSize='small'
          onClick={onClick}
          label={props.condensed ? 'Sample' : sampleButtonText}
          displayType='button'
          fullWidth
        />
      );
    }
    return (
      <ChirpButton
        class='play_sample_button'
        fontSize={props.fontSize}
        icon={props.hideIcon ? null : icon}
        iconAltText={props.condensed ? sampleButtonText : ''}
        iconSize='small'
        onClick={onClick}
        label={props.condensed ? 'Sample' : sampleButtonText}
        displayStyle={props.underlined ? 'underline' : 'gray'}
        displayType={props.pillStyle ? 'pill' : 'link'}
      />
    );
  }

  function renderAudio() {
    return (
      <audio ref={audioRef} onEnded={onEnded} preload='none' src={audiobook.sampleUrl} />
    );
  }

  if (_.isNil(audiobook.sampleUrl)) {
    return null;
  }

  return (
    <>
      {renderButton()}
      {renderAudio()}
    </>
  );
}

PlaySampleButton.propTypes = {
  audiobook: ChirpPropShapes.audiobook,
  backgroundColor: PropTypes.string,
  buttonStyle: PropTypes.oneOf(['default', 'audiobookList']),
  condensed: PropTypes.bool,
  fontSize: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large']),
  hideIcon: PropTypes.bool,
  hoverColor: PropTypes.string,
  libraryLink: PropTypes.string,
  onPauseSample: PropTypes.func,
  onPlaySample: PropTypes.func,
  origin: PropTypes.string,
  pauseText: PropTypes.string,
  pillStyle: PropTypes.bool,
  playText: PropTypes.string,
  shouldPause: PropTypes.bool,
  trackingLocation: PropTypes.string.isRequired,
  underlined: PropTypes.bool,
};

PlaySampleButton.defaultProps = {
  buttonStyle: 'default',
  condensed: false,
  fontSize: 'medium',
  hideIcon: false,
  onPauseSample: () => {},
  onPlaySample: () => {},
  pauseText: 'Pause Sample',
  pillStyle: false,
  playText: 'Play Sample',
  shouldPause: false,
  underlined: false,
};
